import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Download, LogOut, RefreshCw, Users, MailCheck, MailX, Settings as SettingsIcon, Send, Upload,
} from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_ORDER, CATEGORY_LABEL, LOGO_URL } from "@/data/quiz";

const emailBadge = (status) => {
  const map = {
    sent: "bg-[#eef6f6] text-[#1d4447]",
    failed: "bg-[#fdeee0] text-[#9b221d]",
    not_configured: "bg-[#fdf7e7] text-[#9e7016]",
    pending: "bg-[#f2eee9] text-[#5a686b]",
  };
  return map[status] || "bg-[#f2eee9] text-[#5a686b]";
};

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resultFilter, setResultFilter] = useState("");
  const [marketingFilter, setMarketingFilter] = useState("");
  const [tab, setTab] = useState("submissions");
  const [settings, setSettings] = useState({ prospectus_url: "", booking_url: "", privacy_url: "", from_name: "", reply_to: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (resultFilter) params.result = resultFilter;
      if (marketingFilter) params.marketing = marketingFilter;
      const [subsRes, statsRes, configRes, settingsRes] = await Promise.all([
        api.get("/admin/submissions", { params }),
        api.get("/admin/stats"),
        api.get("/admin/config-status"),
        api.get("/admin/settings"),
      ]);
      setSubs(subsRes.data);
      setStats(statsRes.data);
      setConfig(configRes.data);
      setSettings(settingsRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  }, [resultFilter, marketingFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (resultFilter) params.set("result", resultFilter);
      if (marketingFilter) params.set("marketing", marketingFilter);
      const token = localStorage.getItem("weave_admin_token");
      const res = await fetch(`${API}/admin/submissions/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "weave-submissions.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export the CSV.");
    }
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/admin/submissions/${id}/resend-email`);
      if (data.email_status === "sent") toast.success("Email sent.");
      else toast.error(data.email_error || "Email could not be sent.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Retry failed.");
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Settings saved.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadProspectus = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/prospectus", fd);
      setSettings(data);
      toast.success("Prospectus uploaded and now live.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const statCards = stats
    ? [
        { label: "Total submissions", value: stats.total, icon: Users, color: "#4aa0a4" },
        { label: "Opted in to marketing", value: stats.opted_in, icon: MailCheck, color: "#3b868a" },
        { label: "Email issues", value: stats.email_failed, icon: MailX, color: "#e93e36" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#faf8f5]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-white border-b border-[#e2ded7]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="The Weave" className="h-7 w-auto" />
            <span className="text-sm font-medium text-[#5a686b] hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#5a686b]">{admin?.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm font-medium text-[#5a686b] hover:text-[#1e292b] transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {config && !config.email_configured && (
          <div className="mb-6 rounded-xl bg-[#fdeee0] border border-[#e93e36]/30 p-4 text-sm text-[#9b221d]" data-testid="config-warning-email">
            Email credentials are not configured. Delivery emails cannot be sent until this is set up on the server.
          </div>
        )}
        {config && (!config.prospectus_url_set || !config.booking_url_set || !config.privacy_url_set) && (
          <div className="mb-6 rounded-xl bg-[#fdf7e7] border border-[#f4b847]/50 p-4 text-sm text-[#9e7016]" data-testid="config-warning-urls">
            Some links still need setting up:{" "}
            {[
              !config.prospectus_url_set && "Prospectus URL",
              !config.booking_url_set && "Booking URL",
              !config.privacy_url_set && "Privacy Notice URL",
            ]
              .filter(Boolean)
              .join(", ")}
            . Visitors won’t see a broken button — configure these in Settings.
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {statCards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-white border border-[#e2ded7] p-5 flex items-center gap-4" data-testid={`stat-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-full" style={{ backgroundColor: `${c.color}1a` }}>
                <c.icon className="h-5 w-5" style={{ color: c.color }} />
              </span>
              <div>
                <p className="text-2xl font-semibold text-[#1e292b]">{c.value}</p>
                <p className="text-sm text-[#5a686b]">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6 border-b border-[#e2ded7]">
          <button
            onClick={() => setTab("submissions")}
            data-testid="tab-submissions"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "submissions" ? "border-[#4aa0a4] text-[#1e292b]" : "border-transparent text-[#5a686b] hover:text-[#1e292b]"}`}
          >
            Submissions
          </button>
          <button
            onClick={() => setTab("settings")}
            data-testid="tab-settings"
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "settings" ? "border-[#4aa0a4] text-[#1e292b]" : "border-transparent text-[#5a686b] hover:text-[#1e292b]"}`}
          >
            <SettingsIcon className="h-4 w-4" /> Settings
          </button>
        </div>

        {tab === "submissions" && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <select
                value={resultFilter}
                onChange={(e) => setResultFilter(e.target.value)}
                data-testid="filter-result"
                className="rounded-xl border-2 border-[#e2ded7] bg-white px-4 py-2.5 text-sm focus:border-[#4aa0a4] focus:outline-none"
              >
                <option value="">All results</option>
                {CATEGORY_ORDER.map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                ))}
              </select>
              <select
                value={marketingFilter}
                onChange={(e) => setMarketingFilter(e.target.value)}
                data-testid="filter-marketing"
                className="rounded-xl border-2 border-[#e2ded7] bg-white px-4 py-2.5 text-sm focus:border-[#4aa0a4] focus:outline-none"
              >
                <option value="">All marketing preferences</option>
                <option value="yes">Opted in</option>
                <option value="no">Opted out</option>
              </select>
              <button onClick={load} data-testid="refresh-button" className="inline-flex items-center gap-2 text-sm font-medium text-[#5a686b] hover:text-[#1e292b] px-3 py-2.5 rounded-lg hover:bg-[#e2ded7]/40 transition-colors">
                <RefreshCw className="h-4 w-4" /> Refresh
              </button>
              <button onClick={exportCsv} data-testid="admin-export-csv-button" className="ml-auto inline-flex items-center gap-2 bg-[#4aa0a4] text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[#3b868a] transition-all shadow-sm active:scale-[0.98]">
                <Download className="h-4 w-4" /> Export CSV
              </button>
            </div>

            <div className="rounded-2xl bg-white border border-[#e2ded7] overflow-hidden">
              {loading ? (
                <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4aa0a4]" /></div>
              ) : subs.length === 0 ? (
                <p className="p-12 text-center text-sm text-[#5a686b]" data-testid="no-submissions">No submissions yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="submissions-table">
                    <thead>
                      <tr className="bg-[#f2eee9] text-left text-xs uppercase tracking-wider text-[#5a686b]">
                        <th className="px-4 py-3 font-semibold">Date</th>
                        <th className="px-4 py-3 font-semibold">Name</th>
                        <th className="px-4 py-3 font-semibold">Email</th>
                        <th className="px-4 py-3 font-semibold">Leading priority</th>
                        <th className="px-4 py-3 font-semibold">Scores (F/M/G/C/B)</th>
                        <th className="px-4 py-3 font-semibold">Marketing</th>
                        <th className="px-4 py-3 font-semibold">Email</th>
                        <th className="px-4 py-3 font-semibold"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {subs.map((s) => (
                        <tr key={s.id} className="border-t border-[#e2ded7]" data-testid={`submission-row-${s.id}`}>
                          <td className="px-4 py-3 whitespace-nowrap text-[#5a686b]">{new Date(s.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</td>
                          <td className="px-4 py-3 text-[#1e292b]">{s.first_name || "—"}</td>
                          <td className="px-4 py-3 text-[#1e292b]">{s.email}</td>
                          <td className="px-4 py-3 text-[#1e292b]">{(s.leading || []).map((c) => CATEGORY_LABEL[c]).join(", ")}</td>
                          <td className="px-4 py-3 text-[#5a686b] whitespace-nowrap">{CATEGORY_ORDER.map((c) => s.scores?.[c] ?? 0).join(" / ")}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${s.marketing_choice === "yes" ? "bg-[#eef6f6] text-[#1d4447]" : "bg-[#f2eee9] text-[#5a686b]"}`}>
                              {s.marketing_choice === "yes" ? "Opted in" : "Opted out"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${emailBadge(s.email_status)}`}>{s.email_status}</span>
                          </td>
                          <td className="px-4 py-3">
                            {s.email_status !== "sent" && (
                              <button onClick={() => resend(s.id)} data-testid={`resend-${s.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-[#4aa0a4] hover:text-[#3b868a]">
                                <Send className="h-3.5 w-3.5" /> Retry
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {tab === "settings" && (
          <div className="max-w-2xl space-y-6">
            <div className="rounded-2xl bg-white border border-[#e2ded7] p-6 sm:p-8" data-testid="prospectus-upload-card">
              <h3 className="text-base font-medium text-[#1e292b]">Personal Clarity Prospectus (PDF)</h3>
              <p className="mt-1 text-sm leading-relaxed text-[#5a686b]">
                Upload a PDF to host it here. This becomes the prospectus used on the results page and in
                every delivery email — uploading again replaces it with the new version.
              </p>
              {settings.prospectus_filename && (
                <p className="mt-3 text-sm text-[#1e292b]" data-testid="current-prospectus-file">
                  Current file: <span className="font-medium">{settings.prospectus_filename}</span>
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <input
                  ref={fileRef}
                  id="prospectus-file"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={uploadProspectus}
                  className="hidden"
                  data-testid="prospectus-file-input"
                />
                <label
                  htmlFor="prospectus-file"
                  className="inline-flex items-center gap-2 cursor-pointer bg-[#4aa0a4] text-white font-medium px-5 py-2.5 rounded-full hover:bg-[#3b868a] transition-all active:scale-[0.98]"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Uploading…" : "Upload new PDF"}
                </label>
                {settings.prospectus_url && (
                  <a
                    href={settings.prospectus_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="preview-prospectus-link"
                    className="text-sm text-[#4aa0a4] underline underline-offset-2 hover:text-[#3b868a]"
                  >
                    Preview current prospectus
                  </a>
                )}
              </div>
            </div>

            <form onSubmit={saveSettings} className="rounded-2xl bg-white border border-[#e2ded7] p-6 sm:p-8 space-y-5" data-testid="settings-form">
            {[
              { key: "prospectus_url", label: "Prospectus PDF URL (auto-set when you upload above, or paste an external link)", placeholder: "https://…/prospectus.pdf" },
              { key: "booking_url", label: "20-minute conversation booking URL", placeholder: "https://…/book" },
              { key: "waitlist_url", label: "Academy waitlist URL", placeholder: "https://…" },
              { key: "privacy_url", label: "Privacy Notice URL", placeholder: "https://…/privacy" },
              { key: "from_name", label: "Email sender name", placeholder: "The Weave Founder Academy" },
              { key: "reply_to", label: "Reply-to email address", placeholder: "Hello@wearetheweave.co.uk" },
            ].map((f) => (
              <div key={f.key}>
                <label htmlFor={f.key} className="block text-sm font-medium text-[#1e292b] mb-1.5">{f.label}</label>
                <input
                  id={f.key}
                  type="text"
                  value={settings[f.key] || ""}
                  onChange={(e) => setSettings((p) => ({ ...p, [f.key]: e.target.value }))}
                  data-testid={`settings-${f.key.replace(/_/g, "-")}`}
                  placeholder={f.placeholder}
                  className="w-full rounded-xl border-2 border-[#e2ded7] bg-white px-4 py-3 text-base focus:border-[#4aa0a4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4aa0a4]/25 transition-colors"
                />
              </div>
            ))}
            <p className="text-xs text-[#5a686b]">Prospectus, booking and privacy links must be full https:// URLs.</p>
            <button type="submit" disabled={savingSettings} data-testid="settings-save-button" className="inline-flex items-center gap-2 bg-[#4aa0a4] text-white font-medium px-6 py-3 rounded-full hover:bg-[#3b868a] transition-all shadow-md active:scale-[0.98] disabled:opacity-50">
              {savingSettings && <Loader2 className="h-4 w-4 animate-spin" />} Save settings
            </button>
          </form>
          </div>
        )}
      </main>
    </div>
  );
}
